// ================================================================================================
// Web Node
// --------
// We handle the web node and manage all "modules/plugins"
// ================================================================================================
import _ from "lodash";
import { SimpleEventDispatcher } from "strongly-typed-events";
import { IWidgetDataValidateResp } from "@UI/Widgets/WidgetStructs";
import { isDefined, isString, IsNullOrWhiteSpace, RunSafe } from "@Utils/Micro";
import { StateManager } from "@Utils/StateManager";
import { DebugEx } from "@Utils/DebugEx";
import { setGlobal } from "@Utils/Globals";
import type { LastUpdateTs } from '@UI/Widgets/Things/Utils/LastUpdateTs';
import { Plegma } from './Api';
import { PlegmaStatics } from './PlegmaStatics';
import { ErrorCode, ErrorType, Keys, WS, WSConf } from './WS'
import {
    DBGMsgsRoot, PlegmaKeys, ReduxActions, ReduxPayloadAction,
    ReduxThingState, StatePayloadAction, StatePayloadActionLoading, StatePayloadActionPort,
    StateStatus, WebNodeStatus, WebThingKeys
} from './WebNodeTypes';

import type { WebModule } from './WebModule';
import type { WebThing, WebThingPort } from './WebThing';
import { yHas } from "@Utils/Utils";
// ================================================================================================
export class WebNode {
    private static _instanceDict: { [key: string]: WebNode } = {};
    // --------------------------------------------------------------------------------------------
    public static GetInstanceKey(nodeKey: string): WebNode { return (_.has(this._instanceDict, nodeKey)) ? this._instanceDict[nodeKey] : null; }
    // --------------------------------------------------------------------------------------------
    public onError: (error: ErrorType, ecode: ErrorCode, msg: any) => void;
    public onStarted: (status: boolean) => void;
    private onStartedCalled: boolean;

    // Keys API
    public onKeysUpdate: (keys: Keys, redirectURL: string) => void;
    public onUnpaired: (reasonCode: Plegma.eUnpairReason, message: string) => void;

    public onAssignedEndpoint: (endpoint: string) => void;
    public get endpointKey(): string { return this.AssignedEndpoint; }

    public YodiWS: WS;
    public AutoPairingInError: boolean;
    public Status: WebNodeStatus;
    public onStatusUpdate: SimpleEventDispatcher<WebNodeStatus>;
    public onGenericRsp: SimpleEventDispatcher<number>;
    public onDataPortUpdate: SimpleEventDispatcher<ReduxPayloadAction[]>;

    public get NodeKey(): string | null { return this.YodiWS.NodeKey; }

    // DBG root
    public DBGMsgsRoot: DBGMsgsRoot = { Things: {} };
    /**
     * Don't propagate any events from widget and avoid to propagate receive port events
     */
    public MsgDisableMode: boolean;
    /**
     * If true, the node won't port events through ws
     */
    public DummyNode: boolean;
    /**
     * if `DummyNode` this callback will be triggered when we sent port events to cloud
     */
    public DummyNodePortEventsOutCB: (thingKey: string, events: Plegma.PortEvent[]) => void;
    // --------------------------------------------------------------------------------------------
    private Modules: { [key: string]: WebModule };
    private Things2Modules: { [key: string]: string };
    private PortKey2Port: { [key: string]: WebThingPort };
    private ActivePortKeys: PlegmaKeys[];
    private AssignedEndpoint: string;
    // --------------------------------------------------------------------------------------------
    // Redux staffs
    public StateManager: StateManager<ReduxThingState, StatePayloadAction>;
    // --------------------------------------------------------------------------------------------
    public lastUpdateTs: { [key: string]: LastUpdateTs } = {};
    public Location = null;
    public inEditMode: boolean;
    // --------------------------------------------------------------------------------------------
    constructor(wsConf: WSConf, inEditMode: boolean) {
        // Link the inited webnode as a singleton
        // This is not ideal but we can access the web node from react components
        setGlobal("WebNode", this);

        // Save the webnode in dictionary for e easier access based on keys
        if (wsConf.Keys != null && wsConf.NodeKey != null) {
            WebNode._instanceDict[wsConf.NodeKey] = this;
        }
        this.Modules = {};
        this.Things2Modules = {};
        this.PortKey2Port = {};
        this.ActivePortKeys = [];
        this.inEditMode = inEditMode;
        this.onStartedCalled = false;
        this.AutoPairingInError = true;
        this.Status = WebNodeStatus.Init;
        this.onStatusUpdate = new SimpleEventDispatcher<WebNodeStatus>();
        this.onGenericRsp = new SimpleEventDispatcher<number>();
        this.onDataPortUpdate = new SimpleEventDispatcher<ReduxPayloadAction[]>();
        this.MsgDisableMode = false;

        if (isDefined(wsConf.Keys) && isDefined(wsConf.NodeKey))
            this.StateManager = new StateManager<ReduxThingState, StatePayloadAction>(wsConf.NodeKey,
                {
                    // ------------------------------
                    [ReduxActions.OnActivePortKey]: (state: ReduxThingState, action: StatePayloadActionPort) => {
                        let isChanged = false;
                        if (isDefined(state)) {
                            if (_.has(state.PortStates, action.PortKey)) {
                                let port = state.PortStates[action.PortKey];
                                if (port.IsActive != action.IsActive) {
                                    port.IsActive = action.IsActive;
                                    port.Timestamp = action.Timestamp;
                                    isChanged = true;
                                }
                            }
                        }
                        return { state, isChanged, changedSubKeys: [action.PortKey] };
                    },
                    // ------------------------------
                    [ReduxActions.OnPortEvent]: (state: ReduxThingState, action: StatePayloadActionPort) => {
                        let isChanged = false;
                        let changedSubKeys: string[] = [];
                        if (isDefined(state)) {
                            if (_.has(state.PortStates, action.PortKey)) {
                                let port = state.PortStates[action.PortKey];
                                if (!_.isObject(port.State) ||
                                    port.State !== action.State ||
                                    port.IsActive != action.IsActive) {
                                    port.State = action.State;
                                    port.IsActive = action.IsActive;
                                    port.Timestamp = action.Timestamp;
                                    port.Version++;

                                    // Update thing/port status
                                    port.LastStateStatus = action.LastStateStatus;
                                    state.LastStateStatus = action.LastStateStatus;
                                    isChanged = true;

                                    changedSubKeys.push(action.PortKey);

                                    // Version is going up only in port input !!!!
                                    let pKey = action.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + action.PortKey;
                                    let p = this.PortKey2Port[pKey];
                                    if (isDefined(p) &&
                                        (
                                            p.PlegmaPort.ioDirection == Plegma.ioPortDirection.Input ||
                                            p.PlegmaPort.ioDirection == Plegma.ioPortDirection.InputOutput
                                        ))
                                        state.Version++;
                                }
                            }
                        }
                        return { state, isChanged, changedSubKeys };
                    },
                    // ------------------------------
                    [ReduxActions.AddPort]: (state: ReduxThingState, action: StatePayloadActionPort) => {
                        if (isDefined(state)) {
                            if (yHas(state.PortStates, action.PortKey)) {
                                let port = state.PortStates[action.PortKey];
                                port.IsActive = action.IsActive;
                            } else {
                                state.PortStates[action.PortKey] = { IsActive: action.IsActive, State: action.State, LastStateStatus: StateStatus.NoData, Version: 0 };
                            }
                        } else {
                            // Add the missing thing
                            state = { PortStates: {}, LastStateStatus: StateStatus.NoData, Loading: false, Version: 0 };
                            state.PortStates[action.PortKey] = { IsActive: action.IsActive, State: action.State, LastStateStatus: StateStatus.NoData, Version: 0 };
                        }
                        return { state, isChanged: true, changedSubKeys: [action.PortKey] };
                    },
                    // ------------------------------
                    [ReduxActions.OnLoadingStateEvent]: (state: ReduxThingState, action: StatePayloadActionLoading) => {
                        let isChanged = false;
                        if (isDefined(state) &&
                            state.Loading != action.loading) {
                            state.Loading = action.loading;
                            state.Version++;
                            isChanged = true;
                        }
                        return { state, isChanged };
                    },
                    // ------------------------------
                }
            )

        this._initYodiWS(wsConf);
    }
    // --------------------------------------------------------------------------------------------
    public setInEditMode(inEditMode: boolean) {
        this.inEditMode = inEditMode;
        if (!inEditMode)
            this.DBGMsgsRoot = { Things: {} };
    }
    // --------------------------------------------------------------------------------------------
    // Private Functions
    // --------------------------------------------------------------------------------------------
    private _initYodiWS(wsConf: WSConf) {
        this.YodiWS = new WS(wsConf, []);

        // ----------------------------------------------
        // Set error handling for Websocket
        this.YodiWS.onError = (errorType, errorCode, msg) => {
            DebugEx.TraceError("WS Error: ", errorType, errorCode, msg);

            // Call user for the errors
            if (isDefined(this.onError)) {
                this.onError(errorType, errorCode, msg);
            }

            // Set that the node is not connected
            this._statusUpdate(WebNodeStatus.Disconnected);

            switch (errorType) {
                // ********************
                case ErrorType.Connection:
                    {
                        switch (errorCode) {
                            // ********************
                            case ErrorCode.NotConnected:
                                DebugEx.TraceLog("Not connected");
                                if (this.AutoPairingInError)
                                    this.YodiWS.Start();
                                break;
                            // ********************
                            case ErrorCode.InvalidKeys:
                                DebugEx.TraceLog("Connection closed because of wrong keys");
                                this.YodiWS.Start();
                                break;
                            // ********************
                        }
                    }
                    break;
                // ********************
                case ErrorType.Pairing:
                    if (errorCode == ErrorCode.NotPaired &&
                        this.AutoPairingInError) {
                        this.YodiWS.Pair();
                    }
                    break;
                // ********************
            }
        };
        // ----------------------------------------------
        this.YodiWS.onPortEvents = (events) => {
            DebugEx.TraceVerbose(events);
            let portsActions: ReduxPayloadAction[] = [];
            for (let event of events) {
                let keys = this.SplitPortKey(event.PortKey);
                let portEvent = this.ReceivePortEvent(keys, event);
                if (portEvent)
                    portsActions.push(portEvent);
            }

            // Send redux event
            if (portsActions.length > 0)
                this.DispatchPortEvent(portsActions);
        };
        // ----------------------------------------------
        this.YodiWS.onPortStateGet = (operation, portkeys) => {
            DebugEx.TraceLog("PORT STATE GET", portkeys);
            var portstates: Plegma.PortState[] = [];

            // Pass all webthings
            _.forOwn(this.Modules, (module, moduleKey) => {
                _.forOwn(module.Things, (thing, index) => {
                    _.forOwn(thing.Ports, (port) => {
                        var portstate: Plegma.PortState = {
                            PortKey: null,
                            State: null,
                            IsDeployed: false,
                        };

                        var pk = thing.ThingKey + '-' + port.PortKey;
                        if (operation == Plegma.ePortStateOperation.AllPortStates ||
                            (portkeys != null && portkeys.indexOf(pk) > -1)) {
                            portstate.PortKey = pk;
                            portstate.State = port.State;
                            portstate.IsDeployed = true
                            portstates.push(portstate);
                        }
                    });
                });
            });

            return portstates;
        };
        // ----------------------------------------------
        this.YodiWS.onKeysUpdate = (keys, redirectURL) => {
            /*
             Handle received secrets. You may:
             - save them (encrypted!), or
             - discard them; the app will happily continue this session without issues,
                and will re-pair at the next refresh / redeploy
             */
            if (isDefined(this.onKeysUpdate))
                this.onKeysUpdate(keys, redirectURL);
        };
        // ----------------------------------------------
        this.YodiWS.onUnpaired = (reason, msg) => {
            /*
             Get informed of node's unpairing. From this point on, and until the node is re-paired
             no messages can be successfully exchanged with the Yodiwo Servers
             */
            if (isDefined(this.onUnpaired))
                this.onUnpaired(reason, msg);
        };
        // ----------------------------------------------
        this.YodiWS.onActivePortKeys = (activePortKeys) => {

            // If the started is called then start processing active ports
            if (!this.onStartedCalled)
                return;

            // Split the port keys based on things
            var thingKeys2Ports: { [key: string]: string[] } = {};
            activePortKeys.forEach(apk => {
                // Find the port keys
                var keys = this.SplitPortKey(apk);
                var portkeys: string[];
                if (yHas(thingKeys2Ports, keys.ThingKey)) {
                    portkeys = thingKeys2Ports[keys.ThingKey];
                } else {
                    portkeys = [];
                    thingKeys2Ports[keys.ThingKey] = portkeys;
                }

                portkeys.push(keys.PortKey);

                // Save the active ports
                this.ActivePortKeys.push(keys);
            });

            let portsStateActions: { key: string, data: StatePayloadActionPort }[] = [];

            // Send enable/disable to each port if its enable
            _.forOwn(this.getPortKey2Port(), (port, key) => {
                let keys = this.SplitPortKey(key);
                let completeKey = WebNode.CreateKey(keys);
                //  Update the local port struct
                let isActive = _.includes(activePortKeys, completeKey);

                // Fill the events
                portsStateActions.push({
                    key: keys.ThingKey,
                    data: {
                        ThingKey: keys.ThingKey,
                        PortKey: keys.PortKey,
                        IsActive: isActive
                    }
                });
            });

            // Update states
            this.StateManager.RunMultipleReduce(
                ReduxActions.OnActivePortKey,
                portsStateActions
            );
        };
        // ----------------------------------------------
        this.YodiWS.onConnected = (nodeKey) => {
            if (isDefined(this.onStarted) && !this.onStartedCalled) {
                this.onStarted(true);
            }
            this._statusUpdate(WebNodeStatus.Connected);
            this.onStartedCalled = true;
        }
        // ----------------------------------------------
        this.YodiWS.onAssignedEndpoint = (assignedEndpoint) => {
            this.AssignedEndpoint = assignedEndpoint;
            DebugEx.TraceLog("Assigned Endpoint: " + assignedEndpoint);
            if (isDefined(this.onAssignedEndpoint)) {
                this.onAssignedEndpoint(assignedEndpoint);
            }
            this._statusUpdate(WebNodeStatus.Connected);
        }
        // ----------------------------------------------
        this.YodiWS.onGenericRsp = (status) => {
            this.onGenericRsp.dispatchAsync(status);
        }
        // ----------------------------------------------
    }
    // --------------------------------------------------------------------------------------------
    private ReceivePortEvent(keys: PlegmaKeys, event: Plegma.PortEvent): ReduxPayloadAction {
        // find the port that get this event
        let portkey = keys.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + keys.PortKey;
        let portKey2Port = this.getPortKey2Port();
        if (_.has(portKey2Port, portkey)) {
            let p = portKey2Port[portkey];

            // We don't have any transport state we just need to push it to array
            // Update the port state of the internal represenation
            p.State = event.State;
            return {
                ThingKey: keys.ThingKey,
                PortKey: keys.PortKey,
                State: p.State,
                IsActive: this.IsPortActive(keys.ThingKey, keys.PortKey),
                Timestamp: event.Timestamp,
                event: event
            };
        }

        return null;
    }
    // --------------------------------------------------------------------------------------------
    private _statusUpdate(newStatus: WebNodeStatus) {
        this.Status = newStatus;
        this.onStatusUpdate.dispatchAsync(newStatus);
    }
    // --------------------------------------------------------------------------------------------
    private canBuildDictionary = false;
    private rebuildDictionaryCb() {
        let oldPortKey2Port = this.PortKey2Port;
        this.PortKey2Port = {};
        let portsStateActions: { key: string, data: StatePayloadActionPort }[] = [];
        for (let mKey in this.Modules) {
            let module = this.Modules[mKey];
            for (let tkey in module.Things) {
                let tvalue = module.Things[tkey];
                for (let pKey in tvalue.Ports) {
                    let p = tvalue.Ports[pKey];
                    let newKey = tkey + PlegmaStatics.PlegmaAPI.KeySeparator + p.PortKey;
                    this.PortKey2Port[newKey] = p;

                    // If is new fill the actions
                    if (oldPortKey2Port[newKey] == null) {
                        portsStateActions.push({
                            key: tkey,
                            data: {
                                ThingKey: tkey,
                                PortKey: p.PortKey,
                                IsActive: true,
                                State: p.State
                            }
                        });
                    }
                }
            }
        }
        // Update states
        this.StateManager.RunMultipleReduce(
            ReduxActions.AddPort,
            portsStateActions
        );
    };
    // --------------------------------------------------------------------------------------------
    public initDictionary() {
        this.canBuildDictionary = true;
        this._rebuildDictionary();
    }
    // --------------------------------------------------------------------------------------------
    private _rebuildDictionary() {
        if (this.canBuildDictionary)
            this.rebuildDictionaryCb();
    }
    // --------------------------------------------------------------------------------------------
    private getPortKey2Port() {
        return this.PortKey2Port;
    }
    // --------------------------------------------------------------------------------------------
    private _sendThingsToServer(overwrite: boolean) {
        if (this.DummyNode) return;

        DebugEx.TraceLog("Send things to cyan", overwrite);

        // Create the things list
        let plegmaThings: Plegma.Thing[] = _.map(this.Modules, m => _.map(m.Things, x => x.PlegmaThing)).flat();

        // Send the data to the server
        this.YodiWS.SetThings(plegmaThings, overwrite ? Plegma.eThingsOperation.Overwrite : Plegma.eThingsOperation.Update);
    }
    // --------------------------------------------------------------------------------------------
    // Public API
    // --------------------------------------------------------------------------------------------
    public ThingLoadingStatusUpdate(states: { thingKey: string, loading: boolean }[]) {
        if (!isDefined(this.StateManager) ||
            !isDefined(states) ||
            states.length == 0)
            return;

        let portsStateActions: { key: string, data: StatePayloadActionLoading }[] = [];
        for (let s of states) {
            portsStateActions.push({
                key: s.thingKey,
                data: {
                    loading: s.loading
                }
            })
        }
        // Update states
        this.StateManager.RunMultipleReduce(
            ReduxActions.OnLoadingStateEvent,
            portsStateActions
        );
    }
    // --------------------------------------------------------------------------------------------
    public Start() {
        // Start connection of ws
        this.YodiWS.Start();
    }
    // --------------------------------------------------------------------------------------------
    public Reconfigure(wsConf: WSConf) {
        this.YodiWS.SetConfigurations(wsConf);
        // Restart pairing after stop :)
        // TODO: find better way for re-connection
        this.AutoPairingInError = true;
        this.YodiWS.Stop();
    }
    // --------------------------------------------------------------------------------------------
    public AddModule(module: WebModule) {
        try {
            this.Modules[module.ModuleKey] = module;
        } catch (ex) {
            DebugEx.TraceException(ex, "Failed to add module in node");
        }
    }
    // --------------------------------------------------------------------------------------------
    public RemoveModuleByKey(moduleKey: string) {
        if (_.has(this.Modules, moduleKey)) {
            this.RemoveModule(this.Modules[moduleKey]);
        }
    }
    // --------------------------------------------------------------------------------------------
    public RemoveModule(module: WebModule) {
        try {
            // Remove the module
            delete this.Modules[module.ModuleKey];

            if (!this.DummyNode) {
                // Send delete request to server
                let removedThings = _.values(module.Things);
                if (removedThings.length > 0) {
                    DebugEx.TraceLog("Send delete request to cyan", removedThings);

                    // Create the things list
                    let plegmaThings: Plegma.Thing[] = _.map(removedThings, t => t.PlegmaThing);

                    // Send the data to the server
                    WebNode
                        .GetInstanceKey(this.NodeKey)
                        .YodiWS.SetThings(plegmaThings,
                            Plegma.eThingsOperation.Delete,
                            module.Hierarchy);
                }
            }
            this._rebuildDictionary();
        } catch (ex) {
            DebugEx.TraceException(ex, "Failed to remove module in node");
        }
    }
    // --------------------------------------------------------------------------------------------
    public RemoveAllModules() {// default is not-overwrite
        try {
            // Clean any local modules
            this.Modules = {};

            // Rebuild and send to server
            this._rebuildDictionary();
        } catch (ex) {
            DebugEx.TraceException(ex, "Failed to remove module in node");
        }
    }
    // --------------------------------------------------------------------------------------------
    public SendUpdateToServer() {
        this._sendThingsToServer(false);
    }
    // --------------------------------------------------------------------------------------------
    public SendOverwriteToServer() {
        this._sendThingsToServer(true);
    }
    // --------------------------------------------------------------------------------------------
    public UpdateModuleThings(module: WebModule) {
        // TODO: Check if we have duplicates things
        try {
            // Create module mapping
            if (!this.DummyNode) {
                this.Things2Modules = _.pickBy(this.Things2Modules, (v, k) => v != module.ModuleKey);
            }
            _.forEach(module.Things, t => this.Things2Modules[t.ThingKey] = module.ModuleKey);

            // Rebuild dictionary
            this._rebuildDictionary();
        } catch (ex) {
            DebugEx.TraceException(ex, "Failed to Update things from module:" + module.ModuleKey);
        }
    }
    // --------------------------------------------------------------------------------------------
    // thingKey, portKey
    public GetThingPort(thingKey: string, portKey: string): WebThingPort | null {
        let key = thingKey + PlegmaStatics.PlegmaAPI.KeySeparator + portKey;
        let portKey2Port = this.getPortKey2Port();
        if (_.has(portKey2Port, key))
            return portKey2Port[key];
        return null
    }
    // key= <ThingKey>
    public GetThing(key: string): WebThing | null {
        // Find modules
        let moduleKey = this.Things2Modules[key];
        if (moduleKey != null) {
            let module = this.Modules[moduleKey];
            return module?.Things[key];
        }
        return null
    }
    // key= <ModuleKey>
    public GetModule(key: string): WebModule | null {
        if (_.has(this.Modules, key))
            return this.Modules[key];
        return null
    }
    // --------------------------------------------------------------------------------------------
    private DispatchPortEvent(Ports: ReduxPayloadAction[]) {
        // Store the events to global database for easy debug
        const processedPorts: ReduxPayloadAction[] = [];
        for (let p of Ports) {
            if (p == null)
                continue;

            let errorMsg = null;
            let dataValidation: IWidgetDataValidateResp = { valid: true, errors: null };
            let moduleKey = this.Things2Modules[p.ThingKey];
            let thing = this.Modules[moduleKey]?.Things[p.ThingKey];
            let port = thing.Ports[p.PortKey];

            // update proccessed ports
            processedPorts.push(p);

            // Parse and validate the data
            if (port.PlegmaPort.Type == Plegma.ePortType.JsonString &&
                isString(p.State)) {
                try {
                    let s = JSON.parse(p.State);
                    if (isDefined(port.cbPortDataValidation))
                        dataValidation = port.cbPortDataValidation(s);

                    // Save the parse object and not the initial string
                    if (dataValidation.valid)
                        p.State = s;
                } catch (ex) {
                    errorMsg = ex.message;
                }
            } else {
                if (isDefined(port.cbPortDataValidation))
                    dataValidation = port.cbPortDataValidation(p.State);
            }

            let ts = new Date().toISOString();
            let lastUpdateTs = this.lastUpdateTs[p.ThingKey];

            // Update the last timestamp (if exists).
            if (lastUpdateTs != null && !lastUpdateTs.props.explicitTs)
                lastUpdateTs.setState({ ts: ts });

            // Store the validation status to state manager
            if (errorMsg)
                p.LastStateStatus = StateStatus.JsonError;
            else if (!dataValidation.valid)
                p.LastStateStatus = StateStatus.DataNotValid;
            else
                p.LastStateStatus = StateStatus.Valid;

            // Update port internal state
            port.State = p.State;
            port.LastStateStatus = p.LastStateStatus;

            // Send the event to the port directly
            if (isDefined(port.onPortEvent))
                port.onPortEvent(p.event, port);

            // In edit mode save the states history
            if (this.inEditMode) {
                // If the thing is not exist add it
                if (!yHas(this.DBGMsgsRoot.Things, p.ThingKey))
                    this.DBGMsgsRoot.Things[p.ThingKey] = { Ports: {} };

                let thing = this.DBGMsgsRoot.Things[p.ThingKey];
                // If the port is not exist add it
                if (!yHas(thing.Ports, p.PortKey))
                    thing.Ports[p.PortKey] = { LastStateHaveErrors: false, States: [] };

                let portState = thing.Ports[p.PortKey];
                portState.LastStateHaveErrors = (errorMsg == null) ? !dataValidation.valid : true;

                // Keep the latest 10
                if (portState.States.length > 10)
                    portState.States.pop();

                portState.States.unshift({
                    ErrorMsg: errorMsg,
                    State: p.State,
                    OrigState: p.OrigState,
                    DataValidation: dataValidation,
                    Timestamp: ts
                });
            }
        }

        // Update states
        this.StateManager.RunMultipleReduce(ReduxActions.OnPortEvent,
            _.map(processedPorts, p => {
                return {
                    key: p.ThingKey,
                    data: {
                        ThingKey: p.ThingKey,
                        PortKey: p.PortKey,
                        IsActive: p.IsActive,
                        State: p.State,
                        OrigState: p.OrigState,
                        LastStateStatus: p.LastStateStatus,
                        Timestamp: p.Timestamp
                    }
                }
            }));

        // Send event to any register member
        if (this.MsgDisableMode !== true)
            this.onDataPortUpdate.dispatchAsync(processedPorts);
    }
    // --------------------------------------------------------------------------------------------
    public SendPortEvent(thing: WebThing, port: WebThingPort, state: any) {
        if (this.MsgDisableMode === true)
            return;
        let msg: Plegma.PortEvent = {
            PortKey: thing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + port.PortKey,
            State: state,
        };
        if (this.DummyNode !== true)
            this.YodiWS.SendPortEvent(msg);
        else if (isDefined(this.DummyNodePortEventsOutCB))
            RunSafe(() => this.DummyNodePortEventsOutCB(thing.ThingKey, [msg]));
        // Dispatch the event back to state
        this.DispatchPortEvent([{
            ThingKey: thing.ThingKey,
            PortKey: port.PortKey,
            State: msg.State,
            IsActive: true
        }]);
    }
    // --------------------------------------------------------------------------------------------
    public SendPortEventsPlegma(events: Plegma.PortEvent[]) {
        if (this.MsgDisableMode === true)
            return;

        if (this.DummyNode !== true)
            this.YodiWS.SendPortEvents(events);

        // Dispatch the event back to state
        this.DispatchPortEvent(_.map(events, x => {
            if (IsNullOrWhiteSpace(x.PortKey))
                return null;
            let parts = x.PortKey.split(PlegmaStatics.PlegmaAPI.KeySeparator);
            if (parts.length != 2)
                return null;
            return {
                ThingKey: parts[0],
                PortKey: parts[1],
                State: x.State,
                OrigState: null,
                IsActive: true
            }
        }));
    }
    // --------------------------------------------------------------------------------------------
    public SendPortEvents(thing: WebThing,
        port: WebThingPort,
        state: any,
        origState: any,
        events: Plegma.PortEvent[]) {
        if (this.MsgDisableMode === true)
            return;

        if (this.DummyNode !== true)
            this.YodiWS.SendPortEvents(events);
        else if (isDefined(this.DummyNodePortEventsOutCB))
            RunSafe(() => this.DummyNodePortEventsOutCB(thing.ThingKey, events));

        // Dispatch the event back to state
        this.DispatchPortEvent([{
            ThingKey: thing.ThingKey,
            PortKey: port.PortKey,
            State: state,
            OrigState: origState,
            IsActive: true
        }]);
    }
    // --------------------------------------------------------------------------------------------
    // Public Static API
    // --------------------------------------------------------------------------------------------
    public WidgetUpdateData(thingKey: string, portKey: string, data: any) {
        let portEvent = this.ReceivePortEvent(
            {
                NodeKey: this.NodeKey,
                PortKey: portKey,
                ThingKey: thingKey
            },
            {
                PortKey: portKey,
                State: data,
                Timestamp: (new Date()).getTime()
            });

        // Send redux event
        if (portEvent != null)
            this.DispatchPortEvent([portEvent]);
    }
    // --------------------------------------------------------------------------------------------
    public UpdateLanguageCode(code: string) {
        if (this.DummyNode) return;

        _.forEach(this.Modules, m => {
            _.forEach(m.Things, (t) => {
                let p = t.Ports[WebThingKeys.BoardLanguageKey];
                if (isDefined(p)) {
                    p.State = code;
                }
            });
        });
    }
    // --------------------------------------------------------------------------------------------
    public UpdateLocation(location: any) {
        if (this.DummyNode) return;

        this.Location = location;
        let locationStr = JSON.stringify(location);
        _.forEach(this.Modules, m => {
            _.forEach(m.Things, (t) => {
                let p = t.Ports[WebThingKeys.Location];
                if (isDefined(p)) {
                    p.State = locationStr;
                }
            });
        });
    }
    // --------------------------------------------------------------------------------------------
    public UpdateCookie(cookie: { [key: string]: any }) {
        if (this.DummyNode) return;

        // update cookie port states 
        _.forEach(this.Modules, m => {
            _.forEach(m.Things, (t) => {
                let p = t.Ports[WebThingKeys.Cookie];
                if (isDefined(p)) {
                    p.State = (cookie != null) ? JSON.stringify(cookie) : null;
                }
            });
        });
    }
    // --------------------------------------------------------------------------------------------
    // Public Static API
    // --------------------------------------------------------------------------------------------
    public SplitPortKey(portKey: string): PlegmaKeys | null {
        if (portKey == null)
            return null;
        let nodeKey = this.YodiWS.CyanNodeKey;

        if (portKey.startsWith(nodeKey)) {
            let thinkPortKey = portKey.substring(nodeKey.length + 1);
            let subIndex = thinkPortKey.lastIndexOf(PlegmaStatics.PlegmaAPI.KeySeparator);
            return {
                NodeKey: nodeKey,
                ThingKey: thinkPortKey.substring(0, subIndex),
                PortKey: thinkPortKey.substring(subIndex + 1) // Remove seperator
            };
        } else {
            // the port key is not conatin the node key
            let subIndex = portKey.lastIndexOf(PlegmaStatics.PlegmaAPI.KeySeparator);
            return {
                NodeKey: nodeKey,
                ThingKey: portKey.substring(0, subIndex),
                PortKey: portKey.substring(subIndex + 1) // Remove seperator
            };
        }
    }
    // --------------------------------------------------------------------------------------------
    public static CreateKey(keys: PlegmaKeys): string {
        return keys.NodeKey + PlegmaStatics.PlegmaAPI.KeySeparator + keys.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + keys.PortKey;
    }
    // --------------------------------------------------------------------------------------------
    private IsPortActive(thingkey: string, portkey: string): boolean {
        try {
            let state = this.StateManager.GetState(thingkey);
            if (isDefined(state))
                return state.PortStates[portkey].IsActive;
        } catch (ex) { }
        return false;
    }
}
// ================================================================================================